import { useEffect, useRef, useState } from 'react'
import { INACTIVITY_TIMEOUT } from '../utils/constants'

export function useInactivityLock({ unlockCode }) {
  const [isLocked, setIsLocked] = useState(false)
  const [unlockInput, setUnlockInput] = useState('')
  const [shakeError, setShakeError] = useState(false)
  const lastActivityRef = useRef(Date.now())

  // Track user activity and lock after inactivity
  useEffect(() => {
    const resetActivity = () => {
      lastActivityRef.current = Date.now()
    }

    const checkInactivity = () => {
      const elapsed = Date.now() - lastActivityRef.current
      if (elapsed >= INACTIVITY_TIMEOUT && !isLocked) {
        setIsLocked(true)
        setUnlockInput('')
      }
    }

    // Track activity events
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll']
    events.forEach(event => window.addEventListener(event, resetActivity))

    // Check for inactivity every 10 seconds
    const inactivityInterval = setInterval(checkInactivity, 10000)

    return () => {
      events.forEach(event => window.removeEventListener(event, resetActivity))
      clearInterval(inactivityInterval)
    }
  }, [isLocked])

  // Handle unlock code entry
  const handleUnlockDigit = (digit) => {
    if (unlockInput.length >= 6) return

    const newInput = unlockInput + digit
    setUnlockInput(newInput)

    // Check if code is complete
    if (newInput.length === 6) {
      // No code configured - unlock immediately
      if (!unlockCode) {
        setIsLocked(false)
        setUnlockInput('')
        lastActivityRef.current = Date.now()
      } else if (newInput === unlockCode) {
        // Correct code
        setIsLocked(false)
        setUnlockInput('')
        lastActivityRef.current = Date.now()
      } else {
        // Wrong code - shake and clear
        setShakeError(true)
        setTimeout(() => {
          setShakeError(false)
          setUnlockInput('')
        }, 500)
      }
    }
  }

  const handleUnlockClear = () => {
    setUnlockInput('')
  }

  const handleUnlockBackspace = () => {
    setUnlockInput(prev => prev.slice(0, -1))
  }

  // If no code is configured, allow unlock on any touch
  const handleScreenTouch = () => {
    if (!unlockCode) {
      setIsLocked(false)
      lastActivityRef.current = Date.now()
    }
  }

  return {
    isLocked,
    setIsLocked,
    unlockInput,
    shakeError,
    handleUnlockDigit,
    handleUnlockClear,
    handleUnlockBackspace,
    handleScreenTouch
  }
}
